import { useEffect, useState, useCallback } from 'react';
import { UserAddress } from '../../../../clients/quipli-service/user/user';
import { useCustomerAPI } from '../../../../clients/customer-api/customer-api-client';
import { DeliveryLocation } from '../../../../clients/quipli-service/delivery/delivery';

// Use a sentinel value to distinguish between "new address" and "edit address"
const NEW_ADDRESS_SENTINEL = 'NEW_ADDRESS' as const;
type EditableAddress = UserAddress | typeof NEW_ADDRESS_SENTINEL | undefined;

export type UseAddressesManagementProps = {
  type: 'billing' | 'delivery';
  addresses: UserAddress[];
  enableSelect: boolean;
  selectedAddress?: UserAddress;
  onSelect?: (address?: UserAddress) => void;
  onOpenForm?: () => void;
  reload?: () => Promise<void>;
};

export type UseAddressesManagementReturn = {
  // State
  isInitializing: boolean;
  isLocationsModuleEnabled: boolean;
  deliveryLocations: DeliveryLocation[];
  deleteModalState: { isOpen: boolean; addressId: number | null };

  // Actions
  handleOpenForm: (address?: UserAddress) => void;
  handleCloseForm: () => void;
  handleConfirmForm: () => Promise<void>;
  openDeleteModal: (addressId: number) => void;
  closeDeleteModal: () => void;
  handleDeleteAddress: () => Promise<void>;

  // Computed values
  getFormAddress: () => UserAddress | undefined;
  isFormVisible: boolean;
};

export const useAddressesManagement = ({
  type,
  addresses,
  enableSelect,
  selectedAddress,
  onSelect,
  onOpenForm,
  reload,
}: UseAddressesManagementProps): UseAddressesManagementReturn => {
  const { getConfiguration, listDeliveryLocations, deleteUserAddress } = useCustomerAPI();

  // State
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [isLocationsModuleEnabled, setIsLocationsModuleEnabled] = useState<boolean>(false);
  const [deliveryLocations, setDeliveryLocations] = useState<DeliveryLocation[]>([]);
  const [editableAddress, setEditableAddress] = useState<EditableAddress>(undefined);
  const [deleteModalState, setDeleteModalState] = useState<{ isOpen: boolean; addressId: number | null }>({
    isOpen: false,
    addressId: null,
  });

  // Open the form to edit or create an address
  const handleOpenForm = useCallback((address?: UserAddress) => {
    // If no address provided, use sentinel for "new address" mode
    setEditableAddress(address || NEW_ADDRESS_SENTINEL);
    onOpenForm?.();
  }, []);

  // Close the form
  const handleCloseForm = useCallback(() => {
    // If there are addresses, reset the editable address to undefined
    // The useEffect will handle address selection when editableAddress becomes undefined
    if (addresses.length > 0) {
      setEditableAddress(undefined);
    }
  }, [addresses.length]);

  // Confirm the form
  const handleConfirmForm = useCallback(async () => {
    await reload?.();
    // Call onSelect when the edited address is the selected address
    if (editableAddress !== NEW_ADDRESS_SENTINEL && editableAddress?.id === selectedAddress?.id) {
      onSelect?.(editableAddress);
    }
    setEditableAddress(undefined);
  }, [editableAddress, selectedAddress?.id]);

  // Open the delete modal
  const openDeleteModal = useCallback((addressId: number) => {
    setDeleteModalState({ isOpen: true, addressId });
  }, []);

  // Close the delete modal
  const closeDeleteModal = useCallback(() => {
    setDeleteModalState({ isOpen: false, addressId: null });
  }, []);

  // Delete an address
  const handleDeleteAddress = useCallback(async () => {
    if (deleteModalState.addressId) {
      await deleteUserAddress(deleteModalState.addressId);
      await reload?.();
      closeDeleteModal();
    }
  }, [deleteModalState.addressId, closeDeleteModal]);

  // Select the default or existing address
  const selectDefaultOrExisting = useCallback(() => {
    // If select is not enabled, return
    if (!enableSelect) return;

    // Find the existing selected address
    const existingSelectedAddress = selectedAddress
      ? addresses.find((a) => a.id === selectedAddress.id)
      : addresses.find((a) => a.isDefault) || addresses[0];

    if (existingSelectedAddress) {
      onSelect?.(existingSelectedAddress);
    }
  }, [enableSelect, addresses, selectedAddress, onSelect]);

  // Initialize settings
  useEffect(() => {
    const getSettings = async () => {
      setIsInitializing(true);
      try {
        const isEnabled = (await getConfiguration('CONF_DELIVERY_LOCATIONS_MODULE'))?.value === '1';
        setIsLocationsModuleEnabled(isEnabled);

        if (type === 'delivery' && isEnabled) {
          const dl = await listDeliveryLocations();
          setDeliveryLocations(dl);
        }
      } catch (error) {
        // Handle errors gracefully
        setIsLocationsModuleEnabled(false);
        setDeliveryLocations([]);
      } finally {
        setIsInitializing(false);
      }
    };

    getSettings();
  }, [type]);

  // Handle address selection when not in form mode
  useEffect(() => {
    if (!editableAddress) {
      addresses.length > 0 ? selectDefaultOrExisting() : handleOpenForm();
    }
  }, [editableAddress, addresses.length, selectDefaultOrExisting, handleOpenForm]);

  // Computed values
  const isFormVisible = editableAddress !== undefined;
  // Helper to get the address for the form (undefined for new, UserAddress for edit)
  const getFormAddress = (): UserAddress | undefined => {
    return editableAddress === NEW_ADDRESS_SENTINEL ? undefined : editableAddress;
  };

  return {
    // State
    isInitializing,
    isLocationsModuleEnabled,
    deliveryLocations,
    deleteModalState,

    // Actions
    handleOpenForm,
    handleCloseForm,
    handleConfirmForm,
    openDeleteModal,
    closeDeleteModal,
    handleDeleteAddress,

    // Computed values
    isFormVisible,
    getFormAddress,
  };
};
